import { useEffect, useState } from "react";
import { Outlet, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

/**
 * Gedeelde guard voor alle ingelogde routes.
 *
 * De Supabase-sessie leeft in localStorage, dus de server weet niet wie de
 * gebruiker is. We renderen daarom niets tot de sessie client-side bekend is;
 * dat voorkomt een flits van beschermde content of van de uitgelogde staat.
 */
function AuthenticatedLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [status, setStatus] = useState<"checking" | "authed" | "anon">("checking");

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setStatus(data.session ? "authed" : "anon");
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setStatus(session ? "authed" : "anon");
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (status !== "anon") return;
    const redirect = encodeURIComponent(pathname);
    navigate({ to: "/auth", search: { redirect } as never, replace: true });
  }, [status, pathname, navigate]);

  if (status !== "authed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label="Laden"
        />
      </div>
    );
  }

  return <Outlet />;
}
